import { ReactNode } from 'react';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { LoyaltyMembership } from '@mews-commander/data-access';
import { SpacedForwardSlash } from '@mews-commander/ui';
import { compactArray } from '@mews/fp';
import { Optional } from '@mews/std';
import { SkeletonText, Stack } from '@optimus-web/core';
import { FeatureFlag } from '@mews-commander/enums';
import { useFeatureFlag } from '@mews/framework';
import { useGuestReservationCounts, useGuestTotalStaysByIds } from '../../../../../accounts/modules/guestInsight';
import { LoyaltyMembershipInfo } from './LoyaltyMembershipInfo';
import { TooltipedReturningIcon } from './TooltippedReturningIcon';
import { CompactedCustomerClassification } from './CompactedCustomerClassification';

interface NewComponentProps {
    loyaltyMembership: Optional<LoyaltyMembership>;
    classifications: React.ReactNode[];
    customerId: string;
}

export const ClassificationsLabel = ({ customerId, loyaltyMembership, classifications }: NewComponentProps) => {
    const optimizedReturningClassificationEnabled = useFeatureFlag(FeatureFlag.optimizedReturningClassificationEnabled);

    const { data: optimizedData, isFetching: isFetchingOptimizedData } = useGuestReservationCounts({
        guestIds: [customerId],
        enabled: optimizedReturningClassificationEnabled,
    });
    const { data, isFetching } = useGuestTotalStaysByIds([customerId], {
        enabled: !optimizedReturningClassificationEnabled,
    });

    const totalStays = optimizedReturningClassificationEnabled
        ? optimizedData?.guestReservationCounts.reservationCounts[0]?.reservationCount
        : data?.guestSummaryByIds[0]?.analytics?.totalStays;

    const membershipInfo = loyaltyMembership ? <LoyaltyMembershipInfo loyaltyMembership={loyaltyMembership} /> : null;
    const returningIcon = totalStays != null ? <TooltipedReturningIcon totalStays={totalStays} /> : null;
    const classificationsComponent =
        classifications.length > 0 ? <CompactedCustomerClassification classifications={classifications} /> : null;

    const renderItems = pipe(
        compactArray([membershipInfo, returningIcon, classificationsComponent]),
        array.intersperse<ReactNode>(SpacedForwardSlash)
    );

    return <Stack spacing="50">{isFetching || isFetchingOptimizedData ? <SkeletonText /> : renderItems}</Stack>;
};
